import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { User } from '../models/user';
import { ClientInfos } from '../models/clientInfos';

interface SignupData {
  name: string;
  ninea: string;
  address: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPhone: string;
  password: string;
  passwordConfirm: string;
  tickets: string[];
  role: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiUrl = environment.backClient;
  private readonly tokenKey = 'token';
  private readonly userKey = 'user';

  private authStatus = new BehaviorSubject<boolean>(this.hasToken());
  private userSubject = new BehaviorSubject<User>(this.getUserFromStorage());
  

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  signup(userData: any, client: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/${client}/register`, userData);
  }

  activateAccount(
    token: string,
    clientType: 'company' | 'merchant' = 'company',
  ): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/${clientType}/activate?token=${token}`,
    );
  }

  private mustChangePasswordSubject = new BehaviorSubject<boolean>(
    this.getMustChangePassword(),
  );

  setMustChangePassword(value: boolean) {
    this.mustChangePasswordSubject.next(value);
    localStorage.setItem('mustChangePassword', JSON.stringify(value));
  }

  getMustChangePassword(): boolean {
    const val = localStorage.getItem('mustChangePassword');
    return val === 'true';
  }

  mustChangePassword$(): Observable<boolean> {
    return this.mustChangePasswordSubject.asObservable();
  }

  changePassword(
    payload: { oldPassword: string; newPassword: string },
    clientType: string,
  ): Observable<any> {
    const token = this.getToken();
    const headers = { Authorization: `Bearer ${token}` };

    return this.http.post(
      `${this.apiUrl}/${clientType}/change-password`,
      payload,
      { headers },
    );
  }

  login(
    credentials: { email: string; password: string },
    client: string,
  ): Observable<any> {
    return this.http
      .post<{
        token: string;
        result: any;
      }>(`${this.apiUrl}/${client}/login`, credentials)
      .pipe(
        tap(({ token, result }) => {
          localStorage.setItem(this.tokenKey, token);
          localStorage.setItem(this.userKey, JSON.stringify(result));
          this.userSubject.next(this.mapToUser(result, token));
          this.authStatus.next(true);

          this.setMustChangePassword(result.mustChangePassword || false);
        }),
      );
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem('mustChangePassword');
    this.authStatus.next(false);
    this.userSubject.next(new User('', null, null, null, null, null, null));
    this.router.navigate(['/signin']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getUser(): Observable<User> {
    return this.userSubject.asObservable();
  }

  isAuthenticated(): Observable<boolean> {
    return this.authStatus.asObservable();
  }

  getUserInfos(clientType: string): Observable<any> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get<any>(`${this.apiUrl}/${clientType}/infos`, {
      headers,
    });
  }

  gtQrCode(sub: any): Observable<any> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get<any>(`${this.apiUrl}/merchant/qr-code/${sub}`, {
      headers,
    });
  }

  private hasToken(): boolean {
    return !!this.getToken();
  }

  getUserFromStorage(): User {
    const rawData = localStorage.getItem(this.userKey);
    const token = this.getToken();

    if (!rawData || !token) {
      return new User('', null, null, null, null, null, null);
    }

    try {
      const data = JSON.parse(rawData);
      return this.mapToUser(data, token);
    } catch {
      return new User('', null, null, null, null, null, null);
    }
  }

  private mapToUser(data: any, token: string): User {
    return new User(
      data?.sub || null,
      data?.email || null,
      data?.phone || null,
      data?.account || null,
      data?.admin || null,
      data?.scopes || null,
      token,
      data?.tickets || null,
    );
  }
}
